import type { OnChangeFn, VisibilityState } from '@tanstack/react-table'
import { useCallback } from 'react'
import {
  LOCAL_STORAGE_SCOPE,
  useLocalStoragePersistence,
} from '@/hooks/use-local-storage-persistence'

interface TablePersistenceConfig {
  tableId: string
  defaultPageSize?: number
  defaultColumnVisibility?: VisibilityState
}

interface TablePersistenceState {
  pageSize: number
  columnVisibility: VisibilityState
}

interface UseTablePersistenceReturn {
  pageSize: number
  columnVisibility: VisibilityState
  setPageSize: (pageSize: number) => void
  setColumnVisibility: (visibility: VisibilityState) => void
  onColumnVisibilityChange: OnChangeFn<VisibilityState>
  resetToDefaults: () => void
  initialPageSize: number
}

/**
 * Hook to manage table configuration persistence in localStorage.
 *
 * Persists column visibility and page size settings per table and page.
 * Uses tableId + current pathname as the storage key to ensure settings
 * are isolated per table and page combination.
 *
 * @param config Configuration object with tableId and optional defaults
 * @returns Object with persisted state and update functions
 */
export function useTablePersistence({
  tableId,
  defaultPageSize = 10,
  defaultColumnVisibility = {},
}: TablePersistenceConfig): UseTablePersistenceReturn {
  const { state, updateState, resetToDefault } = useLocalStoragePersistence<TablePersistenceState>({
    key: `table-config-${tableId}`,
    defaultValue: {
      pageSize: defaultPageSize,
      columnVisibility: defaultColumnVisibility,
    },
    scope: LOCAL_STORAGE_SCOPE.PAGE,
  })

  // Update functions
  const setPageSize = useCallback(
    (pageSize: number) => {
      updateState((prev) => ({ ...prev, pageSize }))
    },
    [updateState],
  )

  const setColumnVisibility = useCallback(
    (columnVisibility: VisibilityState) => {
      updateState((prev) => ({ ...prev, columnVisibility }))
    },
    [updateState],
  )

  const onColumnVisibilityChange = useCallback(
    (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
      updateState((prev) => {
        const newVisibility =
          typeof updater === 'function' ? updater(prev.columnVisibility) : updater
        return { ...prev, columnVisibility: newVisibility }
      })
    },
    [updateState],
  )

  return {
    pageSize: state.pageSize,
    columnVisibility: state.columnVisibility,
    setPageSize,
    setColumnVisibility,
    onColumnVisibilityChange,
    resetToDefaults: resetToDefault,
    initialPageSize: state.pageSize,
  }
}
